import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, CreditCard } from "lucide-react";
import { PaymentModal } from "./PaymentModal";

export function TrialBanner() {
  const {
    tenantId,
    loading,
    subscriptionStatus,
    subscriptionPlanId,
    subscriptionType,
    subscriptionPaymentMethod,
    subscriptionNextBillingDate,
    trialDaysRemaining,
  } = useTenant();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const navigate = useNavigate();

  // Buscar dados do plano se houver subscription_plan_id
  // IMPORTANTE: Hook deve ser chamado ANTES de qualquer return condicional
  const { data: plan } = useQuery({
    queryKey: ["subscription-plan", subscriptionPlanId],
    queryFn: async () => {
      if (!subscriptionPlanId) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("id", subscriptionPlanId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!subscriptionPlanId && !!tenantId && !loading,
  });

  // Não mostrar banner se não tem tenant ou está carregando
  if (!tenantId || loading) return null;

  // Plano ativo
  if (subscriptionPlanId && subscriptionStatus === 'active') {
    const planName = plan?.name || subscriptionPlanId;
    const shouldShowBilling = subscriptionType === 'regular' && subscriptionNextBillingDate;
    
    // Definir cores baseado no tipo
    const bannerColors = subscriptionType === 'cortesia'
      ? {
          bg: "bg-gradient-to-r from-amber-500/10 via-amber-600/10 to-yellow-500/10",
          border: "border-amber-500/20",
          icon: "text-amber-600 dark:text-amber-400",
          text: "text-amber-900 dark:text-amber-100",
          textSecondary: "text-amber-700 dark:text-amber-300",
          badge: "border-amber-600 text-amber-700 dark:text-amber-300"
        }
      : subscriptionType === 'testers'
      ? {
          bg: "bg-gradient-to-r from-blue-500/10 via-cyan-600/10 to-sky-500/10",
          border: "border-blue-500/20",
          icon: "text-blue-600 dark:text-blue-400",
          text: "text-blue-900 dark:text-blue-100",
          textSecondary: "text-blue-700 dark:text-blue-300",
          badge: "border-blue-600 text-blue-700 dark:text-blue-300"
        }
      : {
          bg: "bg-gradient-to-r from-emerald-500/10 via-emerald-600/10 to-emerald-500/10",
          border: "border-emerald-500/20",
          icon: "text-emerald-600 dark:text-emerald-400",
          text: "text-emerald-900 dark:text-emerald-100",
          textSecondary: "text-emerald-700 dark:text-emerald-300",
          badge: "border-emerald-600 text-emerald-700 dark:text-emerald-300"
        };
    
    return (
      <div 
        className={`${bannerColors.bg} border-b ${bannerColors.border} cursor-pointer hover:opacity-90 transition-opacity`}
        onClick={() => navigate('/configuracoes/assinatura')}
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-5 w-5 ${bannerColors.icon}`} />
              <div className="flex items-center gap-2">
                <p className={`text-sm font-semibold ${bannerColors.text}`}>
                  {planName} Ativo
                </p>
                <span className={`text-sm ${bannerColors.textSecondary}`}>
                  •
                </span>
                <p className={`text-sm ${bannerColors.textSecondary}`}>
                  {subscriptionType === 'cortesia' && 'Plano Especial de cortesia - Sem cobrança'}
                  {subscriptionType === 'testers' && 'Plano Beta Tester - Sem cobrança'}
                  {subscriptionType === 'regular' && subscriptionPaymentMethod === 'credit_card' && 'Cobrança automática no cartão'}
                  {subscriptionType === 'regular' && subscriptionPaymentMethod === 'pix' && 'Pagamento realizado via PIX'}
                  {subscriptionType === 'regular' && subscriptionPaymentMethod === 'manual' && 'Plano ativado manualmente'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {shouldShowBilling && (
                <Badge variant="outline" className={`${bannerColors.badge} text-xs py-0.5`}>
                  Próxima cobrança: {new Date(subscriptionNextBillingDate).toLocaleDateString('pt-BR')}
                </Badge>
              )}
              <Badge variant="outline" className={`${bannerColors.badge} text-xs py-0.5`}>
                Premium
              </Badge>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Trial ativo
  if (subscriptionStatus === 'trial' && trialDaysRemaining !== null) {
    const isExpiringSoon = trialDaysRemaining <= 3;
    
    return (
      <>
        <div 
          className={`border-b cursor-pointer hover:opacity-90 transition-opacity ${
            isExpiringSoon 
              ? 'bg-gradient-to-r from-red-500/10 via-red-600/10 to-red-500/10 border-red-500/20'
              : 'bg-gradient-to-r from-amber-500/10 via-amber-600/10 to-amber-500/10 border-amber-500/20'
          }`}
          onClick={() => navigate('/configuracoes/assinatura')}
        >
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1">
                <AlertCircle className={`h-5 w-5 ${
                  isExpiringSoon 
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`} />
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-semibold ${
                    isExpiringSoon
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-amber-900 dark:text-amber-100'
                  }`}>
                    {trialDaysRemaining > 0 
                      ? `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'dia' : 'dias'} restantes no período de teste`
                      : 'Período de teste expirado'}
                  </p>
                  <span className={`text-sm ${
                    isExpiringSoon
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-amber-700 dark:text-amber-300'
                  }`}>
                    •
                  </span>
                  <p className={`text-sm ${
                    isExpiringSoon
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-amber-700 dark:text-amber-300'
                  }`}>
                    {subscriptionType === 'cortesia' 
                      ? 'Entre em contato para ativar seu plano especial de cortesia'
                      : subscriptionType === 'testers'
                      ? 'Entre em contato para ativar seu Plano Beta Tester'
                      : 'Escolha um plano a partir de R$ 49,90/mês'}
                  </p>
                </div>
              </div>
              {subscriptionType === 'regular' && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPaymentModal(true);
                  }}
                  size="sm"
                  className="gap-2 h-7 text-xs"
                  variant={isExpiringSoon ? "destructive" : "default"}
                >
                  <CreditCard className="h-3 w-3" />
                  Assinar Agora
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <PaymentModal 
          open={showPaymentModal} 
          onOpenChange={setShowPaymentModal}
        />
      </>
    );
  }

  // Assinatura cancelada ou suspensa
  if (subscriptionStatus === 'cancelled' || subscriptionStatus === 'suspended') {
    return (
      <>
        <div 
          className="bg-gradient-to-r from-red-500/10 via-red-600/10 to-red-500/10 border-b border-red-500/20 cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => navigate('/configuracoes/assinatura')}
        >
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                    {subscriptionStatus === 'cancelled' ? 'Assinatura Cancelada' : 'Assinatura Suspensa'}
                  </p>
                  <span className="text-sm text-red-700 dark:text-red-300">
                    •
                  </span>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Reative sua assinatura para continuar usando o sistema
                  </p>
                </div>
              </div>
              {subscriptionType === 'regular' && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPaymentModal(true);
                  }}
                  size="sm"
                  variant="destructive"
                  className="gap-2 h-7 text-xs"
                >
                  <CreditCard className="h-3 w-3" />
                  Reativar Plano
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <PaymentModal 
          open={showPaymentModal} 
          onOpenChange={setShowPaymentModal}
        />
      </>
    );
  }

  // Sem plano ativo - mostrar banner para escolher plano
  if (!subscriptionPlanId) {
    const daysRemaining = trialDaysRemaining ?? 0;
    const isExpiringSoon = daysRemaining <= 5;
    
    // Mensagem baseada nos dias restantes
    const message = isExpiringSoon
      ? `Seu período de teste está vencendo • ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'} restante${daysRemaining === 1 ? '' : 's'} • Assine já a partir de R$ 49,90/mês`
      : `Período de Teste • Ainda tem ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'} para testar • Conheça os planos • A partir de R$ 49,00/mês`;

    return (
      <div 
        className={`border-b cursor-pointer hover:opacity-90 transition-opacity ${
          isExpiringSoon
            ? 'bg-gradient-to-r from-orange-500/10 via-amber-600/10 to-orange-500/10 border-orange-500/20'
            : 'bg-gradient-to-r from-blue-500/10 via-cyan-600/10 to-blue-500/10 border-blue-500/20'
        }`}
        onClick={() => navigate('/configuracoes/assinatura')}
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              {isExpiringSoon ? (
                <AlertCircle className={`h-5 w-5 ${
                  isExpiringSoon
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`} />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-sm font-semibold ${
                  isExpiringSoon
                    ? 'text-orange-900 dark:text-orange-100'
                    : 'text-blue-900 dark:text-blue-100'
                }`}>
                  {message}
                </p>
              </div>
            </div>
            {subscriptionType === 'regular' && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/configuracoes/assinatura');
                }}
                size="sm"
                className={`gap-2 h-7 text-xs ${
                  isExpiringSoon
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
                variant={isExpiringSoon ? "default" : "default"}
              >
                <CreditCard className="h-3 w-3" />
                {isExpiringSoon ? 'Assinar Agora' : 'Ver Planos'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
